<template>
    <div id="question-edit-text-modal" :class="[{ 'modal fade p-2': true, show: showModal, close: !showModal }]">
        <div @click="closeModal()" class="bg-backdrop absolute w-full h-full"></div>
        <div class="modal-content w-full h-full flex flex-col relative">
            <div class="modal-header flex justify-between items-center p-4">
                <h3 class="m-0 shadow-text-none">{{ $t('question_modal_header_edit_text_question') }}</h3>
                <i @click="closeModal()" class="fas fa-times text-xl shadow-text-none cursor-pointer"></i>
            </div>
            <div class="modal-body w-full h-full overflow-y-auto font-size-16">
                <div class="edit-options-box md:flex items-center sticky p-4">
                    <div class="select-edit-option-box w-full shadow-text-none">
                        <label for="select-edit-option" class="font-bold">
                            {{ $t('question_modal_select_edit_text_option') }}
                        </label>
                        <XSelect
                            @input="optionSelect = $event"
                            :options="options"
                            :value="optionSelect"
                            :clearable="false"
                            :searchable="false"
                            id="select-edit-option"
                            label="label"
                            class="mt-1 w-full"
                        ></XSelect>
                    </div>
                    <div class="flex-auto mt-4 md:m-0 shadow-text-none">
                        <label class="font-bold">{{ optionSelect.label }}</label>
                        <div v-if="optionSelect.value == 'find_and_replace_word'" class="find-and-replace-box flex">
                            <XInput
                                @input="searchWord = $event.target.value"
                                :value="searchWord"
                                :placeholder="$t('question_modal_search_word')"
                                label="label"
                                class="mt-1 input input-block quiz-dropdown"
                            ></XInput>
                            <XInput
                                @input="replaceWord = $event.target.value"
                                :value="replaceWord"
                                :placeholder="$t('question_modal_replace_word')"
                                label="label"
                                class="mt-1 input input-block quiz-dropdown"
                            ></XInput>
                        </div>

                        <XInput
                            v-if="optionSelect.value == 'add_word_prepend' || optionSelect.value == 'add_word_append'"
                            @input="addWord = $event.target.value"
                            :value="addWord"
                            label="label"
                            class="mt-1 input input-block quiz-dropdown"
                        ></XInput>
                    </div>
                </div>
                <div class="p-4 pt-0 shadow-text-none">
                    <template v-if="cacheQuestionSelect.length">
                        <label for="select-edit-option" class="font-bold">
                            {{ $t('question_modal_question_list') }}
                        </label>
                        <div
                            v-for="(item, index) in cacheQuestionSelect"
                            :key="index"
                            v-html="item.html"
                            class="edit-text-question-item p-4"
                        ></div>
                    </template>
                    <div v-else>{{ $t('question_modal_no_result_edit_text_question', { word: searchWord }) }}</div>
                </div>
            </div>
            <div class="modal-footer flex justify-end p-2">
                <XButton @click="update()" bg-color="#28a745">
                    {{ $t('question_button_update_edit_text_question') }}
                </XButton>
            </div>
        </div>
    </div>
</template>

<script>
import { mapGetters } from 'vuex'
import popup from '@/lib/popup.js'
import XSelect from '@/components/v2/XSelect'
import XInput from '@/components/v2/XInput'
import XButton from '@/components/v2/XButton'

export default {
    components: {
        XSelect,
        XInput,
        XButton
    },

    props: {
        questionSelect: {
            type: Array,
            required: true
        }
    },

    data() {
        return {
            showModal: true,
            cacheQuestionSelect: [],
            searchWord: '',
            replaceWord: '',
            addWord: '',
            optionSelect: {
                label: this.$t('question_modal_find_replace_word'),
                value: 'find_and_replace_word'
            },
            options: [
                {
                    label: this.$t('question_modal_find_replace_word'),
                    value: 'find_and_replace_word'
                },
                {
                    label: this.$t('question_modal_add_prepend_word'),
                    value: 'add_word_prepend'
                },
                {
                    label: this.$t('question_modal_add_append_word'),
                    value: 'add_word_append'
                }
            ]
        }
    },

    created() {
        this.resetCacheQuestion()
    },

    mounted() {
        this.stickyBox()
    },

    computed: {
        ...mapGetters({
            questions: 'getQuestions'
        })
    },

    watch: {
        optionSelect() {
            this.searchWord = ''
            this.replaceWord = ''
            this.addWord = ''
        },

        searchWord() {
            this.highlightText()
        },

        replaceWord() {
            this.highlightText()
        },

        addWord() {
            this.addText()
        }
    },

    methods: {
        async closeModal() {
            this.showModal = false

            setTimeout(() => {
                this.$emit('close')
            }, 300)
        },

        resetCacheQuestion() {
            this.cacheQuestionSelect = this.questionSelect.map(e => {
                return {
                    uid: e.uid,
                    title: e.title,
                    html: e.title
                }
            })
        },

        highlightText() {
            this.resetCacheQuestion()

            if (!this.searchWord) return

            this.cacheQuestionSelect = this.cacheQuestionSelect.filter(e => e.title.includes(this.searchWord))

            if (!this.cacheQuestionSelect.length) return

            if (!this.replaceWord) {
                this.cacheQuestionSelect = this.cacheQuestionSelect.map(e => {
                    return {
                        ...e,
                        html: e.html.replace(this.searchWord, `<span class="highlight-text">${this.searchWord}</span>`)
                    }
                })
            } else {
                this.cacheQuestionSelect = this.cacheQuestionSelect.map(e => {
                    return {
                        ...e,
                        title: e.title.replace(this.searchWord, this.replaceWord),
                        html: e.html.replace(
                            this.searchWord,
                            `<span class="remove-text">${this.searchWord}</span><span class="replace-text">${this.replaceWord}</span>`
                        )
                    }
                })
            }
        },

        addText() {
            this.resetCacheQuestion()

            if (!this.addWord) return

            if (this.optionSelect.value === 'add_word_prepend') {
                this.cacheQuestionSelect = this.cacheQuestionSelect.map(e => {
                    return {
                        ...e,
                        title: `${this.addWord}${e.title}`,
                        html: `<strong>${this.addWord}</strong>${e.html}`
                    }
                })
            }

            if (this.optionSelect.value === 'add_word_append') {
                this.cacheQuestionSelect = this.cacheQuestionSelect.map(e => {
                    return {
                        ...e,
                        title: `${e.title}${this.addWord}`,
                        html: `${e.html}<strong>${this.addWord}</strong>`
                    }
                })
            }
        },

        update() {
            for (const question of this.cacheQuestionSelect) {
                this.questions[this.questions.findIndex(e => e.uid === question.uid)].title = question.title
            }

            popup.alert(this.$t('question_alert_update_edit_text_success'), () => {
                this.showModal = false

                setTimeout(() => {
                    this.$emit('update')
                }, 300)
            })
        },

        stickyBox() {
            const stickyElm = document.querySelector('.edit-options-box')

            const observer = new IntersectionObserver(
                ([e]) => {
                    e.target.classList.toggle('sticky-box-shadow', e.intersectionRatio < 1)
                },
                { threshold: [1] }
            )

            observer.observe(stickyElm)
        }
    }
}
</script>

<style lang="scss">
#question-edit-text-modal {
    .modal-content {
        .modal-body {
            .x-select {
                .vs__selected {
                    color: #000;
                    text-shadow: none;
                }
            }

            .select-edit-option-box {
                max-width: 380px;
            }

            .edit-options-box {
                top: -1px;
                left: 0;
                column-gap: 1rem;
                background-color: #fff;
                z-index: 1;
                transition: box-shadow 0.3s;

                &.sticky-box-shadow {
                    box-shadow: 0px 1px 8px rgba(0, 0, 0, 0.3);
                }

                .find-and-replace-box {
                    column-gap: 1rem;
                }
            }

            .highlight-text {
                background-color: #ffc107;
            }

            .remove-text {
                color: #fff;
                background-color: #dc3545;
                text-decoration: line-through;
            }

            .replace-text {
                color: #fff;
                background-color: #28a745;
            }

            .edit-text-question-item {
                min-height: 55px;
            }

            .edit-text-question-item ~ .edit-text-question-item {
                border-top: 1px solid rgba(0, 0, 0, 0.125);
            }
        }
    }
}
</style>